import { IHttpApi } from '@cian/http-api';
import { ILogger } from '@cian/logger';
import { IContext, ISaveSearchProps } from '@cian/save-search-widget';
import { ModalWindow } from '@cian/ui-kit/modal';
import loadable from '@loadable/component';
import * as React from 'react';

import { IJsonQuery } from '../../../json_query';
import * as styles from './SaveSearchModal.css';
import { WidgetErrorBoundary } from './components/WidgetErrorBoundary';
import { WidgetFallback } from './components/WidgetFallback';

const SaveSearchWidgetLoadable = loadable.lib(() => import('@cian/save-search-widget'), {
  fallback: <WidgetFallback />,
  ssr: false,
});

export interface ISaveSearchModalProps {
  httpApi: IHttpApi;
  logger: ILogger;
  subdomain: string;
  jsonQuery: IJsonQuery;
  title: string | null;
  userEmail: string | null;
  hideLicense?: boolean;
  subscribeToPushAvailable?: boolean;
  open: boolean;
  onPushSubscribe?(): Promise<void>;
  onSubscribed?(parameters: Parameters<Required<ISaveSearchProps>['onSubscribed']>[0]): void;
  onClose(): void;
}

export const SaveSearchModal: React.FC<ISaveSearchModalProps> = ({
  hideLicense,
  httpApi,
  jsonQuery,
  logger,
  open: isOpen,
  subdomain,
  subscribeToPushAvailable,
  title,
  userEmail,
  onClose,
  onPushSubscribe,
  onSubscribed,
}) => {
  const widgetContext = React.useRef<IContext | null>(null);
  const handleRetry = React.useCallback(() => {
    SaveSearchWidgetLoadable.load();
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      widgetContext.current = null;
    }
  }, [isOpen]);

  return (
    <ModalWindow
      open={isOpen}
      onClose={onClose}
      size="M"
      title="Сохранение поиска"
      content={
        <div className={styles['container']}>
          <WidgetErrorBoundary logger={logger} onRetry={handleRetry}>
            <SaveSearchWidgetLoadable>
              {({ createContext, Provider, SaveSearch, EFrequency, ESource }) => {
                if (!widgetContext.current) {
                  widgetContext.current = createContext({
                    subdomain,
                    httpApi,
                    logger,
                  });
                }

                return (
                  <Provider context={widgetContext.current}>
                    <SaveSearch
                      source={ESource.Listing}
                      jsonQuery={jsonQuery}
                      defaultTitle={title || undefined}
                      defaultFrequency={EFrequency.Hourly}
                      defaultEmail={userEmail || undefined}
                      defaultSubscribeToNews={true}
                      defaultSubscribeToPush={true}
                      hideLicense={hideLicense}
                      subscribeToPushAvailable={subscribeToPushAvailable}
                      onPushSubscribe={onPushSubscribe}
                      onSubscribed={onSubscribed}
                    />
                  </Provider>
                );
              }}
            </SaveSearchWidgetLoadable>
          </WidgetErrorBoundary>
        </div>
      }
    />
  );
};
